import axios from "axios";
import * as actionTypes from "./actionTypes";
import { GET_HOME_CATEGORIES_URL, GET_ALL_CATEGORIES_URL, GET_CATEGORY_BY_SLUG_URL, GET_CATEGORIES_WITH_PRODUCTS_URL, SEARCH_CATEGORIES_URL } from "../../configs";
import { categoriesData } from "../../components/Mobile/Phase2/Home/Categories/categoryData";
import { getImageUrl } from "../../helpers/imageUrl";

// Helper function to get location data from localStorage or use defaults
const getLocationData = () => {
  try {
    const savedLocation = localStorage.getItem('user_location');
    if (savedLocation) {
      const location = JSON.parse(savedLocation);
      return {
        latitude: location.latitude || 15.3172775,
        longitude: location.longitude || 75.7138884,
        state: location.state || 'Karnataka'
      };
    }
  } catch (error) {
    console.log('Error parsing location data:', error);
  }
  
  // Default location (Bangalore, Karnataka)
  return {
    latitude: 15.3172775,
    longitude: 75.7138884,
    state: 'Karnataka'
  };
};

// Transform API response to match our data structure
const transformCategoryData = (apiCategories) => {
  return apiCategories.map(category => ({
    id: category.id,
    name: category.name,
    slug: category.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, ''),
    image: category.image,
    placeholder: category.image ? 
      getImageUrl(category.image) : 
      getImageUrl('/assets/img/categories/placeholder.jpg'),
    isHomeCategory: true,
    subcategories: [] // Will be populated when needed
  }));
};

// Get Home Page Categories (for carousel)
export const getHomeCategories = () => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.GET_HOME_CATEGORIES_REQUEST });
    
    try {
      const { latitude, longitude, state } = getLocationData();
      const apiUrl = `${GET_HOME_CATEGORIES_URL}?latitude=${latitude}&longitude=${longitude}&state=${state}&main-categories-16`;
      
      console.log('🏠 Fetching home categories from:', apiUrl);
      
      const response = await axios.get(apiUrl);
      
      if (response.data.success && response.data.main_categories_16) {
        const transformedCategories = transformCategoryData(response.data.main_categories_16);
        
        // For now, if we get less than 16 categories from API, fill with mock data
        let finalCategories = transformedCategories;
        if (transformedCategories.length < 16) {
          const mockCategories = categoriesData.filter(cat => cat.isHomeCategory).slice(transformedCategories.length);
          finalCategories = [...transformedCategories, ...mockCategories.slice(0, 16 - transformedCategories.length)];
        }
        
        dispatch({
          type: actionTypes.GET_HOME_CATEGORIES_SUCCESS,
          payload: finalCategories
        });
        return { success: true, data: finalCategories };
      } else {
        // Fallback to mock data if API fails
        console.log('⚠️ API response invalid, using mock data');
        const mockCategories = categoriesData.filter(cat => cat.isHomeCategory);
        dispatch({
          type: actionTypes.GET_HOME_CATEGORIES_SUCCESS,
          payload: mockCategories
        });
        return { success: true, data: mockCategories };
      }
    } catch (error) {
      console.error('❌ Home categories API error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";
      
      // Fallback to mock data on error
      const mockCategories = categoriesData.filter(cat => cat.isHomeCategory);
      dispatch({
        type: actionTypes.GET_HOME_CATEGORIES_SUCCESS,
        payload: mockCategories
      });
      
      return { success: true, data: mockCategories };
    }
  };
};

// Get All Categories (for All Categories page)
export const getAllCategories = () => {
  return async (dispatch) => {
    console.log('🚀 getAllCategories action called');
    dispatch({ type: actionTypes.GET_ALL_CATEGORIES_REQUEST });
    
    try {
      const { latitude, longitude, state } = getLocationData();
      const apiUrl = `${GET_ALL_CATEGORIES_URL}?latitude=${latitude}&longitude=${longitude}&state=${state}&all-main-categories`;
      
      console.log('📋 GET_ALL_CATEGORIES_URL:', GET_ALL_CATEGORIES_URL);
      console.log('📋 Complete API URL:', apiUrl);
      console.log('📋 Expected URL: http://127.0.0.1:8000/api/v2/home-products?latitude=15.3172775&longitude=75.7138884&state=Karnataka&all-main-categories');
      
      const response = await axios.get(apiUrl);
      
      if (response.data.success && response.data.all_main_categories) {
        const transformedCategories = transformCategoryData(response.data.all_main_categories);
        
        console.log('✅ All categories API response:', response.data.all_main_categories);
        console.log('🔄 Transformed categories:', transformedCategories);
        
        dispatch({
          type: actionTypes.GET_ALL_CATEGORIES_SUCCESS,
          payload: transformedCategories
        });
        return { success: true, data: transformedCategories };
      } else {
        // Fallback to mock data if API response is invalid
        console.log('⚠️ All categories API response invalid, using mock data');
        const mockCategories = categoriesData;
        dispatch({
          type: actionTypes.GET_ALL_CATEGORIES_SUCCESS,
          payload: mockCategories
        });
        return { success: true, data: mockCategories };
      }
    } catch (error) {
      console.error('❌ All categories API error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";
      
      // Fallback to mock data on error
      const mockCategories = categoriesData;
      dispatch({
        type: actionTypes.GET_ALL_CATEGORIES_SUCCESS,
        payload: mockCategories
      });
      
      return { success: true, data: mockCategories };
    }
  };
};

// Transform subcategory data from API response
const transformSubcategoryData = (apiSubcategories) => {
  return apiSubcategories.map(subcategory => ({
    id: subcategory.id,
    name: subcategory.name,
    slug: subcategory.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, ''),
    image: subcategory.image,
    placeholder: subcategory.image ? 
      getImageUrl(subcategory.image) : 
      getImageUrl('/assets/img/categories/placeholder.jpg')
  }));
};

// Get Category by Slug (for subcategories page)
export const getCategoryBySlug = (slug) => {
  return async (dispatch) => {
    console.log('🔍 getCategoryBySlug action called for slug:', slug);
    dispatch({ type: actionTypes.GET_CATEGORY_BY_SLUG_REQUEST });
    
    try {
      const { latitude, longitude, state } = getLocationData();
      const apiUrl = `${GET_CATEGORY_BY_SLUG_URL}?latitude=${latitude}&longitude=${longitude}&state=${state}&main-categories-with-subs`;
      
      console.log('🔍 GET_CATEGORY_BY_SLUG_URL:', GET_CATEGORY_BY_SLUG_URL);
      console.log('🔍 Complete API URL:', apiUrl);
      console.log('🔍 Looking for category with slug:', slug);
      
      const response = await axios.get(apiUrl);
      
      if (response.data.success && response.data.categories_with_subcategories) {
        console.log('✅ Categories with subcategories API response:', response.data.categories_with_subcategories);
        
        // Find the category that matches the slug
        const matchingCategory = response.data.categories_with_subcategories.find(category => {
          const categorySlug = category.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, '');
          console.log('🔍 Comparing slugs:', categorySlug, 'vs', slug);
          return categorySlug === slug;
        });
        
        if (matchingCategory) {
          const transformedSubcategories = transformSubcategoryData(matchingCategory.subcategories || []);
          
          const categoryData = {
            id: matchingCategory.id,
            name: matchingCategory.name,
            slug: slug,
            image: matchingCategory.image,
            placeholder: matchingCategory.image ? 
              getImageUrl(matchingCategory.image) : 
              getImageUrl('/assets/img/categories/placeholder.jpg'),
            subcategories: transformedSubcategories
          };
          
          console.log('✅ Found matching category:', categoryData);
          console.log('🔄 Transformed subcategories:', transformedSubcategories);
          
          dispatch({
            type: actionTypes.GET_CATEGORY_BY_SLUG_SUCCESS,
            payload: categoryData
          });
          return { success: true, data: categoryData };
        } else {
          console.log('⚠️ No matching category found for slug:', slug);
          // Fallback to mock data if category not found
          const mockCategory = categoriesData.find(cat => cat.slug === slug);
          if (mockCategory) {
            dispatch({
              type: actionTypes.GET_CATEGORY_BY_SLUG_SUCCESS,
              payload: mockCategory
            });
            return { success: true, data: mockCategory };
          } else {
            dispatch({
              type: actionTypes.GET_CATEGORY_BY_SLUG_FAILURE,
              payload: "Category not found"
            });
            return { success: false, message: "Category not found" };
          }
        }
      } else {
        // Fallback to mock data if API response is invalid
        console.log('⚠️ Categories with subcategories API response invalid, using mock data');
        const mockCategory = categoriesData.find(cat => cat.slug === slug);
        if (mockCategory) {
          dispatch({
            type: actionTypes.GET_CATEGORY_BY_SLUG_SUCCESS,
            payload: mockCategory
          });
          return { success: true, data: mockCategory };
        } else {
          dispatch({
            type: actionTypes.GET_CATEGORY_BY_SLUG_FAILURE,
            payload: "Category not found"
          });
          return { success: false, message: "Category not found" };
        }
      }
    } catch (error) {
      console.error('❌ Category by slug API error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";
      
      // Fallback to mock data on error
      const mockCategory = categoriesData.find(cat => cat.slug === slug);
      if (mockCategory) {
        dispatch({
          type: actionTypes.GET_CATEGORY_BY_SLUG_SUCCESS,
          payload: mockCategory
        });
        return { success: true, data: mockCategory };
      } else {
        dispatch({
          type: actionTypes.GET_CATEGORY_BY_SLUG_FAILURE,
          payload: errorMessage
        });
        return { success: false, message: errorMessage };
      }
    }
  };
};

// Search Categories (fallback to mock data for now)
export const searchCategories = (searchTerm) => {
  return async (dispatch) => {
    dispatch({ type: actionTypes.SEARCH_CATEGORIES_REQUEST });
    
    try {
      // TODO: Replace with real API when available
      // const response = await axios.get(`${SEARCH_CATEGORIES_URL}?q=${searchTerm}`);
      
      // For now, use mock data search
      console.log(`🔎 Using mock data for search: ${searchTerm}`);
      
      const filteredCategories = categoriesData.filter(cat => 
        cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (cat.subcategories && cat.subcategories.some(sub => 
          sub.name.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );
      
      dispatch({
        type: actionTypes.SEARCH_CATEGORIES_SUCCESS,
        payload: filteredCategories
      });
      return { success: true, data: filteredCategories };
    } catch (error) {
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";
      dispatch({
        type: actionTypes.SEARCH_CATEGORIES_FAILURE,
        payload: errorMessage
      });
      return { success: false, message: errorMessage };
    }
  };
};

// Clear Categories Error
export const clearCategoriesError = () => ({
  type: actionTypes.CLEAR_CATEGORIES_ERROR
});

// Reset Categories State
export const resetCategoriesState = () => ({
  type: actionTypes.RESET_CATEGORIES_STATE
});

// Transform product data from API response
const transformProductData = (apiProducts) => {
  return apiProducts.map((product, index) => {
    // Use real old_price from API (no more mock data)
    const old_price = product.old_price || null;
    
    // Extract restaurant_id from nested restaurant object
    const restaurant_id = product.restaurant ? product.restaurant.id : null;
    
    // Extract item_category_id from nested item_category object
    const item_category_id = product.item_category ? product.item_category.id : null;
    
    console.log(`🎗️ Product ${product.name}: price=${product.price}, old_price=${old_price}`);
    console.log(`🎗️ API badges - is_new: ${product.is_new}, is_top_picks: ${product.is_top_picks}, is_popular: ${product.is_popular}`);
    console.log(`🏪 Product ${product.name}: restaurant_id=${restaurant_id}, item_category_id=${item_category_id}`);
    console.log(`🏪 Restaurant data:`, product.restaurant);
    console.log(`🏪 Item category data:`, product.item_category);
    
    return {
      id: product.id,
      name: product.name,
      price: product.price,
      old_price: old_price,
      image: product.image,
      placeholder: product.image ? 
        getImageUrl(product.image) : 
        getImageUrl('/assets/img/items/placeholder.jpg'),
      brand: product.brand || '',
      description: product.description || '',
      is_active: product.is_active !== undefined ? product.is_active : true,
      stock_status: product.stock_status || 'available',
      is_favorited: product.is_favorited || false,
      addon_categories: product.addon_categories || [],
      stock: product.stock || 100,
      quantity: 1,
      // Include additional fields from API response
      is_veg: product.is_veg,
      restaurant: product.restaurant,
      item_category: product.item_category,
      master_item: product.master_item,
      // EXTRACT NESTED IDs FOR CART FUNCTIONALITY
      restaurant_id: restaurant_id, // Extract from product.restaurant.id
      item_category_id: item_category_id, // Extract from product.item_category.id
      zone_id: product.zone_id || null, // Use zone_id from API or null
      // Convert boolean badge values to numeric (ItemBadge expects 1/0, not true/false)
      is_new: product.is_new ? 1 : 0,
      is_top_picks: product.is_top_picks ? 1 : 0,
      is_popular: product.is_popular ? 1 : 0
    };
  });
};

// Get Categories with Products (for products display page)
export const getCategoriesWithProducts = () => {
  return async (dispatch) => {
    console.log('🛍️ getCategoriesWithProducts action called');
    dispatch({ type: actionTypes.GET_CATEGORIES_WITH_PRODUCTS_REQUEST });
    
    try {
      const { latitude, longitude, state } = getLocationData();
      const apiUrl = `${GET_CATEGORIES_WITH_PRODUCTS_URL}?latitude=${latitude}&longitude=${longitude}&state=${state}&categories-with-products`;
      
      console.log('🛍️ GET_CATEGORIES_WITH_PRODUCTS_URL:', GET_CATEGORIES_WITH_PRODUCTS_URL);
      console.log('🛍️ Complete API URL:', apiUrl);
      console.log('🛍️ Expected URL: http://127.0.0.1:8000/api/v2/home-products?latitude=15.3172775&longitude=75.7138884&state=Karnataka&categories-with-products');
      
      const response = await axios.get(apiUrl);
      
      if (response.data.success && response.data.categories_with_subcategories_and_products) {
        console.log('✅ Categories with products API response:', response.data.categories_with_subcategories_and_products);
        
        // Transform the data to include products in subcategories
        const transformedCategories = response.data.categories_with_subcategories_and_products.map(category => ({
          id: category.id,
          name: category.name,
          slug: category.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, ''),
          image: category.image,
          placeholder: category.image ? 
            getImageUrl(category.image) : 
            getImageUrl('/assets/img/categories/placeholder.jpg'),
          subcategories: category.subcategories.map(subcategory => ({
            id: subcategory.id,
            name: subcategory.name,
            slug: subcategory.name.toLowerCase().replace(/\s+/g, '-').replace(/[()&,]/g, ''),
            image: subcategory.image,
            placeholder: subcategory.image ? 
              getImageUrl(subcategory.image) : 
              getImageUrl('/assets/img/categories/placeholder.jpg'),
            products: transformProductData(subcategory.products || [])
          }))
        }));
        
        console.log('🔄 Transformed categories with products:', transformedCategories);
        
        dispatch({
          type: actionTypes.GET_CATEGORIES_WITH_PRODUCTS_SUCCESS,
          payload: transformedCategories
        });
        return { success: true, data: transformedCategories };
      } else {
        console.log('⚠️ Categories with products API response invalid');
        dispatch({
          type: actionTypes.GET_CATEGORIES_WITH_PRODUCTS_FAILURE,
          payload: "Invalid API response"
        });
        return { success: false, message: "Invalid API response" };
      }
    } catch (error) {
      console.error('❌ Categories with products API error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || "Network error";
      
      dispatch({
        type: actionTypes.GET_CATEGORIES_WITH_PRODUCTS_FAILURE,
        payload: errorMessage
      });
      return { success: false, message: errorMessage };
    }
  };
}; 